import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

export type ProjectVisibilityConfirmDialogsProps = {
  confirmShowOpen: boolean;
  confirmHideOpen: boolean;
  saving: boolean;
  onCancelShow: () => void;
  onConfirmShow: () => void;
  onCancelHide: () => void;
  onConfirmHide: () => void;
};

const title = (
  <>
    <WarningIcon sx={{ color: "#ed6c02" }} fontSize="small" />
    Confirm Project Visibility Change
  </>
);

const ProjectVisibilityConfirmDialogs: React.FC<ProjectVisibilityConfirmDialogsProps> = (props) => {
  const {
    confirmShowOpen,
    confirmHideOpen,
    saving,
    onCancelShow,
    onConfirmShow,
    onCancelHide,
    onConfirmHide,
  } = props;

  return (
    <>
      <Dialog open={confirmShowOpen} onClose={saving ? undefined : onCancelShow}
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, "& .MuiDialog-paper": { marginTop: "80px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {title}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Turning on project visibility will expose all projects, entities,
              deliverables, statuses, and engagement managers to clients.
            </Typography>
            <Typography variant="body2">
              To hide certain data from client view, adjust visibility
              configurations through Project Settings or Project Details.
            </Typography>
          </Stack>
          {saving && (
            <Stack direction="row" sx={{ mt: 2, alignItems: "center" }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <span>Updating project visibility...</span>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCancelShow} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={onConfirmShow}
            disabled={saving}
          >
            {saving ? "Confirming..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmHideOpen} onClose={saving ? undefined : onCancelHide}
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, "& .MuiDialog-paper": { marginTop: "80px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {title}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Turning off project visibility will hide all projects and related data
              from clients.
            </Typography>
            <Typography variant="body2">
              Clients will no longer see projects, associated entities,
              deliverables, statuses, or engagement manager information.
            </Typography>
            <Typography variant="body2">
              You can re-enable visibility any time by toggling Show Projects back
              on.
            </Typography>
          </Stack>
          {saving && (
            <Stack direction="row" sx={{ mt: 2, alignItems: "center" }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <span>Updating project visibility...</span>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCancelHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={onConfirmHide}
            disabled={saving}
          >
            {saving ? "Confirming..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectVisibilityConfirmDialogs;
